import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Truck, Plus, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/LoadingSkeleton";
import { ImageStager } from "@/components/ImageStager";
import { authFetch } from "@/lib/auth";
import { uploadAttachment } from "@/lib/attachments";
import { toast } from "sonner";
import type { Vehicle } from "@shared/types";

export function VehiclesPage() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formName, setFormName] = useState("");
  const [formMake, setFormMake] = useState("");
  const [formModel, setFormModel] = useState("");
  const [formYear, setFormYear] = useState("");
  const [formVin, setFormVin] = useState("");
  const [formOdometer, setFormOdometer] = useState("");
  const [formOdometerUnit, setFormOdometerUnit] = useState<"mi" | "km" | "hr">("mi");
  const [formStatus, setFormStatus] = useState<NonNullable<Vehicle["service_status"]>>("in_service");
  const [formNotes, setFormNotes] = useState("");
  // Staged image picked at create time. Uploaded after the entity has
  // an id; failure is surfaced as a warning toast and does not roll
  // back the create.
  const [imageFile, setImageFile] = useState<File | null>(null);

  const fetchVehicles = async () => {
    try {
      const data = await authFetch<{ items: Vehicle[] }>("/api/vehicles");
      setVehicles(data.items ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load vehicles";
      setError(msg);
      console.error("[Vehicles]", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const filtered = useMemo(() => {
    return vehicles.filter((v) => {
      const haystack = [v.name, v.make, v.model]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchSearch = haystack.includes(search.toLowerCase());
      const matchStatus =
        statusFilter === "all" || v.service_status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [vehicles, search, statusFilter]);

  const resetForm = () => {
    setFormName("");
    setFormMake("");
    setFormModel("");
    setFormYear("");
    setFormVin("");
    setFormOdometer("");
    setFormOdometerUnit("mi");
    setFormStatus("in_service");
    setFormNotes("");
    setImageFile(null);
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      toast.error("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      const created = await authFetch<{ id: string }>("/api/vehicles", {
        method: "POST",
        body: JSON.stringify({
          name: formName.trim(),
          make: formMake.trim() || null,
          model: formModel.trim() || null,
          year: formYear ? parseInt(formYear, 10) : null,
          vin: formVin.trim() || null,
          odometer_value: formOdometer ? parseInt(formOdometer, 10) : null,
          odometer_unit: formOdometer ? formOdometerUnit : null,
          service_status: formStatus,
          notes: formNotes.trim() || null,
        }),
      });

      if (imageFile && created?.id) {
        try {
          await uploadAttachment(imageFile, {
            linked_entity_type: "vehicle",
            linked_entity_id: created.id,
          });
        } catch (uploadErr) {
          toast.warning(
            `Saved, but image upload failed: ${(uploadErr as Error).message ?? "unknown error"}`,
          );
        }
      }

      toast.success("Vehicle added");
      resetForm();
      setDialogOpen(false);
      setLoading(true);
      fetchVehicles();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to add vehicle";
      toast.error(msg);
      console.error("[AddVehicle]", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Vehicles"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Vehicles" }]}
      >
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
              <Plus className="h-4 w-4 mr-2" /> Add Vehicle
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Vehicle</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="v-name">Name *</Label>
                <Input
                  id="v-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Kubota M5-091"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="v-make">Make</Label>
                  <Input
                    id="v-make"
                    value={formMake}
                    onChange={(e) => setFormMake(e.target.value)}
                    placeholder="Kubota"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="v-model">Model</Label>
                  <Input
                    id="v-model"
                    value={formModel}
                    onChange={(e) => setFormModel(e.target.value)}
                    placeholder="M5-091"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="v-year">Year</Label>
                  <Input
                    id="v-year"
                    type="number"
                    value={formYear}
                    onChange={(e) => setFormYear(e.target.value)}
                    placeholder="2022"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="v-vin">VIN</Label>
                  <Input
                    id="v-vin"
                    value={formVin}
                    onChange={(e) => setFormVin(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="v-odo">Odometer / Hours</Label>
                  <Input
                    id="v-odo"
                    type="number"
                    value={formOdometer}
                    onChange={(e) => setFormOdometer(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Select
                    value={formOdometerUnit}
                    onValueChange={(v) =>
                      setFormOdometerUnit(v as "mi" | "km" | "hr")
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mi">mi</SelectItem>
                      <SelectItem value="km">km</SelectItem>
                      <SelectItem value="hr">hr</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Service Status</Label>
                <Select
                  value={formStatus}
                  onValueChange={(v) =>
                    setFormStatus(v as NonNullable<Vehicle["service_status"]>)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_service">In service</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="out_of_service">Out of service</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="v-notes">Notes</Label>
                <Textarea
                  id="v-notes"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Image</Label>
                <ImageStager
                  file={imageFile}
                  onChange={setImageFile}
                  disabled={submitting}
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? "Adding..." : "Add Vehicle"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vehicles..."
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="in_service">In service</SelectItem>
            <SelectItem value="maintenance">Maintenance</SelectItem>
            <SelectItem value="out_of_service">Out of service</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <CardGridSkeleton count={6} />
      ) : error ? (
        <Card className="p-6 text-center text-clay dark:text-clay-dark">{error}</Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Truck}
          title="No vehicles"
          description={
            search || statusFilter !== "all"
              ? "Try adjusting your filters."
              : "Add your first vehicle to get started."
          }
          actionLabel={
            !search && statusFilter === "all" ? "Add Vehicle" : undefined
          }
          onAction={
            !search && statusFilter === "all"
              ? () => setDialogOpen(true)
              : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((v) => {
            const subline = [v.make, v.model, v.year]
              .filter(Boolean)
              .join(" ");
            return (
              <Link key={v.id} to={`/vehicles/${v.id}`}>
                <Card className="p-5 hover:shadow-md transition-shadow duration-200 cursor-pointer group">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Truck className="h-5 w-5 text-ranch-brown group-hover:scale-110 transition-transform" />
                      <h3 className="font-semibold text-foreground">{v.name}</h3>
                    </div>
                    <StatusBadge status={v.service_status} />
                  </div>
                  {subline && (
                    <p className="text-sm text-muted-foreground">{subline}</p>
                  )}
                  {v.odometer_value !== null && v.odometer_value !== undefined && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {v.odometer_value.toLocaleString()} {v.odometer_unit ?? "mi"}
                    </p>
                  )}
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
